/**
 * The same gate as the sidebar's, on the URL.
 *
 * Hiding a nav row does not unregister its route: a bookmark, a browser-history entry or a
 * link in an old email still lands on `/app/accounts/9/waslo/answers`, and on a workspace
 * with no assistant that page can only fail. The nav gate is what an owner sees; this is
 * what she can reach. Neither is redundant, and they read the SAME cached answer
 * (`composables/useWasloProvisioned.js`), so they cannot disagree.
 *
 * `beforeEnter` and not the global guard: `helper/routeHelpers.js` reads `meta.permissions`
 * only, and adding an async lookup to a guard every navigation in Chatwoot passes through
 * would put this section's failure modes on Chatwoot's critical path.
 *
 * REDIRECTS TO `home`, the account's conversation dashboard — the same destination
 * `conversation.routes.js` uses for a navigation it cannot honour. A 404 page would be a
 * lie (the route exists and works elsewhere) and a Waslo-branded "not set up" page would
 * be the section advertising itself to a workspace we have deliberately hidden it from.
 */

import { isWasloProvisioned } from './composables/useWasloProvisioned.js';

/**
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {Promise<true|import('vue-router').RouteLocationRaw>}
 */
export const wasloProvisionedGuard = async to => {
  const accountId = to?.params?.accountId;
  // Cannot happen for these paths — every one of them carries `:accountId` — but a guard
  // that redirected to a route whose own required param is missing would replace a broken
  // page with a broken navigation.
  if (accountId === undefined || accountId === null || accountId === '')
    return true;

  if (await isWasloProvisioned(accountId)) return true;
  return { name: 'home', params: { accountId } };
};

export default wasloProvisionedGuard;
